import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

import { ANALYTICS_QUERY_KEY } from "./useSummary";
import {
  DELETED_IMPORT_JOBS_QUERY_KEY,
  IMPORT_JOBS_QUERY_KEY,
} from "./useImportJobs";
import { TRADES_QUERY_KEY } from "./useTrades";

export type DeleteBatchResult = components["schemas"]["DeleteBatchResultResponse"];

export type DeleteImportBatchVariables = {
  jobId: string;
  includeModified: boolean;
};

/**
 * Soft-delete an import batch. Preserves modified trades by default; the
 * ``includeModified`` toggle also soft-deletes them. Invalidates the recent +
 * trash import lists and the trade/analytics surfaces the removed trades fed.
 */
export function useDeleteImportBatch() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<DeleteBatchResult, Error, DeleteImportBatchVariables>({
    mutationFn: async ({ jobId, includeModified }) => {
      const { data, error } = await client.POST("/api/imports/jobs/{job_id}/delete", {
        params: { path: { job_id: jobId } },
        body: { include_modified: includeModified },
      });
      if (error !== undefined) {
        // Surface the server's reason (FastAPI ``{detail}``) instead of a generic
        // string, so a failed delete is diagnosable rather than silent.
        const detail =
          typeof error === "object" && error !== null && "detail" in error
            ? String((error as { detail: unknown }).detail)
            : "Failed to delete import.";
        throw new Error(detail);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: IMPORT_JOBS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DELETED_IMPORT_JOBS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: TRADES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEY });
    },
  });
}
